import React from 'react';
import { cn } from '../../lib/utils';

export const Input = React.forwardRef(({ 
  className, 
  type = 'text', 
  label, 
  error, 
  required, 
  ...props 
}, ref) => {
  if (label) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-slate-200">
          {label} {required && <span className="text-primary">*</span>}
        </label>
        <input
          type={type}
          ref={ref}
          className={cn(
            'w-full bg-slate-800 border border-slate-700 focus:border-primary h-12 rounded-lg px-4 text-base text-white placeholder:text-slate-500 transition-all focus:outline-none focus:ring-2 focus:ring-primary/20',
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
            className
          )}
          {...props}
        />
        {error && <p className="text-sm text-red-400">{error}</p>}
      </div>
    );
  }
  
  return (
    <input
      type={type}
      ref={ref}
      className={cn(
        'w-full bg-slate-950 border border-slate-800 focus:border-primary h-12 rounded-lg px-4 text-base text-foreground placeholder:text-muted-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20',
        className
      )}
      {...props}
    />
  );
});

Input.displayName = 'Input';

export const Textarea = React.forwardRef(({ 
  className, 
  label, 
  error, 
  required, 
  ...props 
}, ref) => {
  if (label) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-slate-200">
          {label} {required && <span className="text-primary">*</span>}
        </label>
        <textarea
          ref={ref}
          className={cn(
            'w-full bg-slate-800 border border-slate-700 focus:border-primary rounded-lg px-4 py-3 text-base text-white placeholder:text-slate-500 transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 min-h-[80px] resize-y',
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
            className
          )}
          {...props}
        />
        {error && <p className="text-sm text-red-400">{error}</p>}
      </div>
    );
  }
  
  return (
    <textarea
      ref={ref}
      className={cn(
        'w-full bg-slate-950 border border-slate-800 focus:border-primary rounded-lg px-4 py-3 text-base text-foreground placeholder:text-muted-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 min-h-[120px] resize-none',
        className
      )}
      {...props}
    />
  );
});

export const Select = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <select
      ref={ref}
      className={cn(
        'w-full bg-slate-950 border border-slate-800 focus:border-primary h-12 rounded-lg px-4 text-base text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none cursor-pointer',
        className
      )}
      {...props}
    >
      {children}
    </select>
  );
});

Select.displayName = 'Select';

export const Checkbox = React.forwardRef(({ className, label, ...props }, ref) => {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input
        type="checkbox"
        ref={ref}
        className={cn(
          'w-5 h-5 rounded border-slate-700 bg-slate-950 text-primary focus:ring-primary/20 cursor-pointer',
          className
        )}
        {...props}
      />
      <span className="text-slate-300">{label}</span>
    </label>
  );
});

Checkbox.displayName = 'Checkbox';
